import React, { useState, useEffect } from 'react';
import { Link, usePage } from '@inertiajs/react';

export default function MarketingLayout({ children, title }) {
  const { url, props } = usePage();
  const { flash, appName, branding, contactDetails } = props;
  const [theme, setTheme] = useState('light');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Initialize theme from document or localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('schoolplex-theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Set page title dynamically
  useEffect(() => {
    document.title = title ? `${title} | SchoolPlex` : 'SchoolPlex | School Operations Platform';
  }, [title]);

  const toggleTheme = () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
    localStorage.setItem('schoolplex-theme', nextTheme);
    document.documentElement.setAttribute('data-theme', nextTheme);
  };

  return (
    <div className="min-h-screen flex flex-column bg-primary text-primary transition-colors duration-300">
      {/* Navigation Header */}
      <nav className="spx-nav">
        <div className="spx-container spx-nav-container">
          <Link href="/" className="spx-logo-link">
            <img 
              src={theme === 'dark' ? '/logo/schoolplexLogoFinal-02.svg' : '/logo/schoolplexLogoFinal-default.svg'} 
              alt="SchoolPlex" 
              style={{ height: '40px', objectFit: 'contain' }}
            />
          </Link>

          {/* Navigation Links */}
          <ul className={`spx-nav-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <li>
              <Link href="/" className={`spx-nav-link ${url === '/' ? 'active' : ''}`}>Platform</Link>
            </li>
            <li>
              <Link href="/solutions" className={`spx-nav-link ${url.startsWith('/solutions') ? 'active' : ''}`}>Solutions</Link>
            </li>
            <li>
              <Link href="/pricing" className={`spx-nav-link ${url.startsWith('/pricing') ? 'active' : ''}`}>Pricing</Link>
            </li>
            <li>
              <Link href="/contact" className={`spx-nav-link ${url.startsWith('/contact') ? 'active' : ''}`}>Contact</Link>
            </li>
          </ul>

          <div className={`spx-nav-actions ${mobileMenuOpen ? 'open' : ''}`}>
            {/* Theme Toggle Button */}
            <button 
              type="button" 
              className="spx-theme-toggle" 
              onClick={toggleTheme} 
              aria-label="Toggle light and dark mode"
              title="Toggle Light/Dark Mode"
            >
              {theme === 'light' ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.07" x2="5.64" y2="17.64" />
                  <line x1="18.36" y1="6.49" x2="19.78" y2="5.07" />
                </svg>
              )}
            </button>

            <a href="/central/login" className="spx-btn spx-btn-secondary" style={{ padding: '8px 16px', fontSize: '14px' }}>
              Central Login
            </a>
            <a href="/onboarding" className="spx-btn spx-btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }}>
              Start Free Pilot
            </a>
          </div>

          <button 
            type="button" 
            className="spx-menu-toggle" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-fill" style={{ marginTop: '72px' }}>
        {/* Flash Notifications */}
        {flash && (flash.success || flash.warning || flash.error) && (
          <div className="spx-container" style={{ paddingTop: '24px' }}>
            {flash.success && <div className="spx-alert spx-alert-success"><i className="fas fa-check-circle"></i>{flash.success}</div>}
            {flash.error && <div className="spx-alert spx-alert-error"><i className="fas fa-times-circle"></i>{flash.error}</div>}
            {flash.warning && <div className="spx-alert spx-alert-error" style={{ color: '#d97706', borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' }}><i className="fas fa-exclamation-triangle"></i>{flash.warning}</div>}
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer className="spx-footer">
        <div className="spx-container">
          <div className="spx-footer-grid">
            <div>
              <div style={{ marginBottom: '16px' }}>
                <img 
                  src={theme === 'dark' ? '/logo/schoolplexLogoFinal-02.svg' : '/logo/schoolplexLogoFinal-default.svg'} 
                  alt="SchoolPlex" 
                  style={{ height: '36px', objectFit: 'contain' }}
                />
              </div>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>{branding?.positioning}</div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '320px', margin: '0 0 16px 0' }}>
                {branding?.summary}
              </p>
            </div>
            <div>
              <h5 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>Platform</h5>
              <ul className="spx-footer-list">
                <li><Link href="/" className="spx-footer-link">Why SchoolPlex</Link></li>
                <li><Link href="/solutions" className="spx-footer-link">Solutions</Link></li>
                <li><Link href="/pricing" className="spx-footer-link">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h5 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>Support</h5>
              <ul className="spx-footer-list">
                <li><Link href="/contact" className="spx-footer-link">Contact Us</Link></li>
                <li><a href="https://wa.me/2348144676666" target="_blank" rel="noopener noreferrer" className="spx-footer-link">WhatsApp Business</a></li>
              </ul>
            </div>
            <div>
              <h5 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>Central</h5>
              <ul className="spx-footer-list">
                <li><a href="/central/login" className="spx-footer-link">Central Login</a></li>
                <li><a href="/onboarding" className="spx-footer-link">Register School</a></li>
              </ul>
            </div>
          </div>

          <div className="spx-footer-bottom">
            <div style={{ display: 'flex', gap: '24px' }}>
              <Link href="/" className="spx-footer-link">Home</Link>
              <Link href="/solutions" className="spx-footer-link">Solutions</Link>
              <Link href="/pricing" className="spx-footer-link">Pricing</Link>
              <Link href="/contact" className="spx-footer-link">Contact</Link>
            </div>
            <p className="spx-footer-copyright">
              &copy; {new Date().getFullYear()} SchoolPlex. Built for African schools with absolute integrity.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Widget */}
      <a 
        href={contactDetails?.whatsapp || "https://wa.me/2348144676666"} 
        className="spx-whatsapp-float" 
        target="_blank" 
        rel="noopener noreferrer"
        aria-label="Chat with SchoolPlex on WhatsApp"
      >
        <i className="fab fa-whatsapp"></i>
      </a>
    </div>
  );
}
